import { useEffect, useRef } from "react";

// Misty, smoky living orb. Reacts to the coach's voice via amplitudeRef (0..1).
const PALETTE = [
  [110, 140, 196], [78, 118, 176], [86, 168, 192], [126, 108, 184], [168, 188, 226], [60, 90, 150],
];

function spawn(p, size, fresh) {
  const a = Math.random() * Math.PI * 2;
  const r = size * (0.06 + Math.random() * (fresh ? 0.3 : 0.16));
  p.x = Math.cos(a) * r; p.y = Math.sin(a) * r;
  p.vx = Math.cos(a) * (0.05 + Math.random() * 0.18); p.vy = Math.sin(a) * (0.05 + Math.random() * 0.18) - 0.06;
  p.r = size * (0.1 + Math.random() * 0.2);
  p.life = fresh ? Math.random() : 0; p.speed = 0.0018 + Math.random() * 0.003;
  p.c = PALETTE[Math.floor(Math.random() * PALETTE.length)];
  p.spin = (Math.random() - 0.5) * 0.004; p.phase = Math.random() * 10;
  return p;
}

export default function MistOrb({ size = 300, amplitudeRef, state = "idle", testId = "mist-orb" }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = size * 1.6;
    cv.width = W * dpr; cv.height = W * dpr;
    ctx.scale(dpr, dpr);
    const parts = Array.from({ length: 60 }, () => spawn({}, size, true));
    let amp = 0, raf, t = 0;

    const frame = () => {
      t += 1;
      const st = stateRef.current;
      const raw = amplitudeRef?.current ?? 0;
      const ambient = st === "thinking" ? 0.18 + 0.12 * Math.sin(t / 14) : st === "listening" ? 0.08 + 0.05 * Math.sin(t / 30) : 0.05 + 0.04 * Math.sin(t / 50);
      const target = Math.max(raw, ambient);
      amp += (target - amp) * (target > amp ? 0.35 : 0.08);
      const cx = W / 2, cy = W / 2;
      ctx.clearRect(0, 0, W, W);
      ctx.globalCompositeOperation = "lighter";

      // halo + sphere body with a soft luminous rim
      const R = size * (0.46 + amp * 0.05);
      const halo = ctx.createRadialGradient(cx, cy, R * 0.8, cx, cy, size * (0.78 + amp * 0.2));
      halo.addColorStop(0, `rgba(140,170,215,${0.12 + amp * 0.18})`);
      halo.addColorStop(1, "rgba(120,150,190,0)");
      ctx.fillStyle = halo; ctx.fillRect(0, 0, W, W);
      const body = ctx.createRadialGradient(cx - R * 0.2, cy - R * 0.25, 0, cx, cy, R);
      body.addColorStop(0, "rgba(90,110,150,0.10)");
      body.addColorStop(0.75, "rgba(70,90,130,0.10)");
      body.addColorStop(0.93, `rgba(170,195,235,${0.16 + amp * 0.2})`);
      body.addColorStop(1, "rgba(170,195,235,0)");
      ctx.fillStyle = body; ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();

      for (const p of parts) {
        p.life += p.speed * (1 + amp * 2.2);
        if (p.life >= 1) spawn(p, size, false);
        const push = 1 + amp * 1.6;
        const swirl = Math.sin(t * 0.01 + p.phase) * 0.25;
        p.x += (p.vx + swirl * p.vy) * push; p.y += (p.vy - swirl * p.vx) * push;
        const ang = p.spin * push; const nx = p.x * Math.cos(ang) - p.y * Math.sin(ang); p.y = p.x * Math.sin(ang) + p.y * Math.cos(ang); p.x = nx;
        const dist = Math.hypot(p.x, p.y) / size;
        const fade = Math.sin(Math.PI * p.life) * Math.max(0, 1 - Math.max(0, dist - 0.32) * 2.2);
        const rr = p.r * (0.7 + p.life * 0.9) * (1 + amp * 0.45);
        const al = fade * (0.14 + amp * 0.2);
        const [r, g, b] = p.c;
        const gx = cx + p.x, gy = cy + p.y;
        const gr = ctx.createRadialGradient(gx, gy, 0, gx, gy, rr);
        gr.addColorStop(0, `rgba(${r},${g},${b},${al})`);
        gr.addColorStop(0.55, `rgba(${r},${g},${b},${al * 0.35})`);
        gr.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = gr;
        ctx.beginPath(); ctx.arc(gx, gy, rr, 0, Math.PI * 2); ctx.fill();
      }

      // luminous core
      const cr = size * (0.09 + amp * 0.13);
      const core = ctx.createRadialGradient(cx, cy - size * 0.03, 0, cx, cy, cr);
      core.addColorStop(0, `rgba(255,255,255,${0.3 + amp * 0.55})`);
      core.addColorStop(0.45, `rgba(205,225,250,${0.12 + amp * 0.3})`);
      core.addColorStop(1, "rgba(160,190,230,0)");
      ctx.fillStyle = core; ctx.beginPath(); ctx.arc(cx, cy, cr, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [size, amplitudeRef]);

  return (
    <div className="relative pointer-events-none" style={{ width: size, height: size }} data-testid={testId} data-state={state}>
      <canvas ref={canvasRef} className="absolute" style={{ width: size * 1.6, height: size * 1.6, left: -size * 0.3, top: -size * 0.3, filter: "blur(3px)" }} />
    </div>
  );
}
